/**
 * Roblox Database Renderer
 * Renders Lua database tables
 */

import { ExportTemplate } from '../../../../types/exportTemplates';
import { applyNamingConvention } from '../../core/naming';
import { formatValue } from '../../core/formatting';
import { extractFields } from '../../fieldMapping/extraction';
import { groupItemsByCategory } from '../../fieldMapping/processing';
import { LookupContext } from '../../fieldMapping/references';

/**
 * Render database template (Roblox/Lua Database pattern)
 * 
 * @param template - The export template configuration
 * @param items - The items to export
 * @param blueprints - Available blueprints
 * @param types - Optional types data for resolving type references
 * @param tags - Optional tags data for resolving tag references
 * @param allItemsForLookup - Optional all items for resolving glossaryItem references (if different from items)
 */
export function renderRobloxDatabaseTemplate(
  template: ExportTemplate,
  items: any[],
  blueprints: any[],
  types?: any[],
  tags?: any[],
  allItemsForLookup?: any[]
): string {
  // Group items by category
  const categories = groupItemsByCategory(items, blueprints, template.categoryMapping);
  
  // Create lookup context for resolving references
  // Use allItemsForLookup if provided, otherwise fall back to items
  const lookupContext: LookupContext = {
    types,
    tags,
    allItems: allItemsForLookup || items
  };
  
  // Build the rendered output manually (Lua format)
  let output = 'local Database = {\n';
  
  categories.forEach((category, catIndex) => {
    output += `\t${category.name} = {\n`;
    
    category.items.forEach((item, itemIndex) => {
      // Pass category name and lookup context for per-field mapping lookup
      const fields = extractFields(item, template, blueprints, category.name, lookupContext);
      const itemKey = applyNamingConvention(item.name || item.id, template.namingConventions.instanceName);
      
      output += `\t\t["${itemKey}"] = {\n`;
      
      fields.forEach((field, fieldIndex) => {
        // Check for field name override
        let fieldName = applyNamingConvention(field.label, template.namingConventions.fieldName);
        if (template.fieldNameOverrides && item.blueprint_id) {
          const blueprintOverrides = template.fieldNameOverrides[item.blueprint_id];
          if (blueprintOverrides && blueprintOverrides[field.label]) {
            const override = blueprintOverrides[field.label];
            if (override.useExportName && override.exportName) {
              fieldName = override.exportName;
            }
          }
        }
        
        const fieldValue = field.value !== undefined 
          ? formatValue(field.value, field.mappedType, template.platform)
          : field.defaultValue;
        
        output += `\t\t\t["${fieldName}"] = ${fieldValue},\n`;
      });
      
      output += `\t\t},\n`;
    });
    
    output += `\t},\n\n`;
  });
  
  output += '}\n\nreturn Database\n';
  
  console.log('✅ Database template rendered successfully');
  
  return output;
}
